import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  MenuItem,
  Typography
} from '@mui/material';

import Form from '../ui/forms';
import Input from '../ui/forms/fields/input';
import Select from '../ui/forms/fields/select';
import PrimaryButton from '../ui/buttons/primaryButton';

import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { generateSections } from '../../features/event/eventSlice';

// Generate types options: [ 'fixed', 'balanced' ]
const generateTypes = [
  {
    value: 'fixed',
    label: 'Fixed number of sections',
    description: 'Generate a specific number of sections.'
  },
  {
    value: 'balanced',
    label: 'Balanced teams per section',
    description: 'Distribute teams evenly across sections.'
  },
  {
    value: 'section_positional',
    label: 'Section Positional',
    description:
      'Generate sections based on team positions in their sections. All first places go into Section 1, all second places in Section 2 etc.'
  }
];

const schema = yup.object().shape({
  generateType: yup.string().required('Generation type is required'),
  prefix: yup.string().max(20, 'Prefix too long'),
  numberOfSections: yup.number().when('generateType', {
    is: (val) => val === 'fixed',
    then: (schema) =>
      schema.min(1, 'At least one section is required').required('Number of sections is required'),
    otherwise: (schema) => schema.notRequired()
  }),
  teamsPerSection: yup.number().when('generateType', {
    is: (val) => val === 'balanced',
    then: (schema) =>
      schema
        .min(1, 'At least one team per section is required')
        .required('Teams per section is required'),
    otherwise: (schema) => schema.notRequired()
  })
});

// The sizes the sections will come out at, by the same rule the server draws
// them with: sections of teamsPerSection with the teams left over shared out
// from the last section inwards, one each, so 50 teams at 12 per section reads
// 12, 12, 13, 13 and 49 reads 12, 12, 12, 13. Fixed mode spreads its teams the
// same way over the number of sections it is given.
const getSectionSizes = ({ generateType, teamCount, numberOfSections, teamsPerSection }) => {
  if (!teamCount) return [];

  let count = null;
  if (generateType === 'fixed' && numberOfSections) {
    count = Math.max(1, Number(numberOfSections));
  } else if (generateType === 'balanced' && teamsPerSection) {
    const per = Math.max(1, Number(teamsPerSection));
    count = Math.max(1, Math.floor(teamCount / per));
  }

  if (!count) return [];

  const base = Math.floor(teamCount / count);
  const extra = teamCount % count;

  return Array.from({ length: count }, (value, i) => base + (i >= count - extra ? 1 : 0));
};

const AutoGenerateSectionsDialog = ({ event }) => {
  const dispatch = useDispatch();
  const saving = useSelector((state) => state.event.saving);

  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const {
    register,
    handleSubmit,
    watch,
    control,
    reset, // <-- add this
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      generateType: '',
      prefix: '',
      numberOfSections: 1,
      teamsPerSection: 1
    }
  });

  const generateType = watch('generateType');
  const prefix = watch('prefix');

  // Get number of teams from event
  const teamCount = event?.teams?.length || 0;

  const sectionSizes = getSectionSizes({
    generateType,
    teamCount,
    numberOfSections: watch('numberOfSections'),
    teamsPerSection: watch('teamsPerSection')
  });

  const numSectionsCalculated = sectionSizes.length || null;

  const handleFormSubmit = async (data) => {
    // Dispatch action to generate sections
    await dispatch(generateSections({ eventId: event.id, ...data }));
    handleClose();
    reset(); // <-- reset form to default values
  };

  const hasExistingSections = event?.sections?.length > 0;

  return (
    <>
      <PrimaryButton variant="contained" onClick={handleOpen} disabled={teamCount === 0}>
        Generate Sections
      </PrimaryButton>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Auto Generate Sections</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" color="textSecondary" mb={2}>
            {teamCount} team{teamCount === 1 ? '' : 's'} available in this event.
          </Typography>
          {teamCount === 0 ? (
            <Typography color="error" mb={2}>
              You cannot generate sections because there are no teams in this event.
            </Typography>
          ) : (
            <>
              <Input
                label="Section Name Prefix"
                error={errors.prefix}
                disabled={saving}
                {...register('prefix')}
              />
              {teamCount > 0 && numSectionsCalculated && (
                <Typography variant="body2" color="primary" mb={2}>
                  This will generate {numSectionsCalculated} section
                  {numSectionsCalculated === 1 ? '' : 's'}
                  {prefix ? ` (e.g. "${prefix} 1", "${prefix} 2")` : ''}.
                </Typography>
              )}
              {sectionSizes.length > 1 && (
                <Typography variant="body2" color="textSecondary" mb={2}>
                  Sections will hold {sectionSizes.join(', ')} teams.
                </Typography>
              )}
              <Form onSubmit={handleSubmit(handleFormSubmit)}>
                <Select
                  label="Generation Type"
                  name="generateType"
                  control={control}
                  error={errors.generateType}>
                  {generateTypes.map((type) => (
                    <MenuItem
                      key={type.value}
                      value={type.value}
                      disabled={type.value === 'section_positional' && !hasExistingSections}>
                      <Box>
                        <Typography variant="body1">{type.label}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {type.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {generateType === 'section_positional' && !hasExistingSections && (
                  <Typography color="error" mt={1}>
                    Section Positional requires existing sections in the event.
                  </Typography>
                )}
                {generateType === 'fixed' && (
                  <Input
                    label="Number Of Sections"
                    type="number"
                    error={errors.numberOfSections}
                    disabled={saving}
                    {...register('numberOfSections')}
                  />
                )}
                {generateType === 'balanced' && (
                  <Input
                    label="Teams Per Section"
                    type="number"
                    error={errors.teamsPerSection}
                    disabled={saving}
                    {...register('teamsPerSection')}
                  />
                )}
                <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
                  <PrimaryButton type="submit" disabled={saving}>
                    Generate
                  </PrimaryButton>
                  <Button onClick={handleClose} color="primary" disabled={saving}>
                    Close
                  </Button>
                </Box>
              </Form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AutoGenerateSectionsDialog;
